<template>
  <Teleport to="body">
    <button
      v-if="selectionAction"
      class="selection-add-button"
      type="button"
      :style="{ top: `${selectionAction.top}px`, left: `${selectionAction.left}px` }"
      @mousedown.prevent="openSkillExperienceDialog"
      @keydown.esc.prevent="selectionAction = null"
    >
      <font-awesome-icon icon="plus" />
      Add skill
    </button>

    <div
      v-if="isSkillExperienceDialogOpen"
      class="skill-experience-backdrop"
      @click.self="closeSkillExperienceDialog"
    >
      <form
        ref="skillDialogRef"
        class="skill-experience-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="skill-experience-title"
        tabindex="-1"
        @submit.prevent="handleAddSelectedSkill"
        @keydown="handleSkillDialogKeydown"
      >
        <div>
          <p class="skill-experience-dialog__eyebrow">Add to your skills</p>
          <h2 id="skill-experience-title">{{ selectedSkillText }}</h2>
        </div>
        <label>
          <span>Years of experience</span>
          <input
            ref="skillYearsInputRef"
            v-model="selectedSkillYears"
            type="number"
            min="0"
            max="50"
            step="1"
            inputmode="numeric"
            autofocus
            placeholder="0"
          />
        </label>
        <p v-if="skillExperienceError" class="skill-experience-dialog__error" role="alert">
          {{ skillExperienceError }}
        </p>
        <div class="skill-experience-dialog__actions">
          <button class="secondary-button" type="button" :disabled="isAddingSelectedSkill" @click="closeSkillExperienceDialog">
            Cancel
          </button>
          <button class="primary-button" type="submit" :disabled="isAddingSelectedSkill">
            {{ isAddingSelectedSkill ? 'Adding...' : 'Add skill' }}
          </button>
        </div>
      </form>
    </div>

    <Transition name="modal">
      <div v-if="isOpen" class="job-modal-backdrop" @click="closeModal">
        <article
          ref="jobModalRef"
          :class="['job-modal', { 'job-modal--expanded': isDescriptionExpanded }]"
          role="dialog"
          :aria-modal="isSkillExperienceDialogOpen ? undefined : 'true'"
          :aria-hidden="isSkillExperienceDialogOpen ? 'true' : undefined"
          :inert="isSkillExperienceDialogOpen || undefined"
          aria-labelledby="job-detail-title"
          tabindex="-1"
          @click.stop
          @keydown="handleJobModalKeydown"
        >
          <header class="job-header">
            <div class="job-header__main">
              <div class="company-mark" aria-hidden="true">{{ companyInitials }}</div>
              <div class="job-header__copy">
                <p class="job-company">{{ job?.company }}</p>
                <h1 id="job-detail-title">{{ job?.title }}</h1>
                <p v-if="job?.date_posted" class="job-posted">Posted {{ formatDate(job.date_posted) }}</p>
              </div>
            </div>

            <div class="job-header__actions">
              <div v-if="matchPercentage !== null" class="match-score">
                <span>{{ matchPercentage }}%</span>
                <small>skill match</small>
              </div>
              <button ref="mainCloseButtonRef" class="icon-button" type="button" aria-label="Close job details" @click="closeModal">
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </header>

          <div v-if="jobFacts.length" class="job-facts" aria-label="Job summary">
            <span v-for="fact in jobFacts" :key="fact.key" class="job-fact">
              <font-awesome-icon :icon="fact.icon" class="job-fact__icon" />
              {{ fact.value }}
            </span>
          </div>

          <ApplicationJourney
            :steps="localProcessSteps"
            :current-status="currentStatus"
            :found-date="job?.date_posted || job?.application_date || job?.latest_status_date || ''"
            :expanded="isApplicationPanelOpen"
            :toggleable="canAddStep"
            @toggle="isApplicationPanelOpen = !isApplicationPanelOpen"
          >
            <template #form>
              <div id="application-step-panel" class="status-form">
                <div class="status-form__fields">
                  <label class="status-form__status">
                    <span>Next step</span>
                    <select v-model="selectedStatus">
                      <option value="">Select next step</option>
                      <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
                    </select>
                  </label>

                  <label>
                    <span>Date</span>
                    <input v-model="statusDate" type="date" />
                  </label>

                  <label>
                    <span>Time</span>
                    <select v-if="!isCustomTime" v-model="selectedTimeSlot">
                      <option value="">Not set</option>
                      <option v-for="slot in timeSlots" :key="slot.value" :value="slot.value">
                        {{ slot.label }}
                      </option>
                      <option value="custom">Custom</option>
                    </select>
                    <span v-else class="custom-time">
                      <input v-model="customTime" type="time" />
                      <button type="button" @click="useTimeSlots">Reset</button>
                    </span>
                  </label>

                  <label class="status-form__note">
                    <span>Note <small>Optional</small></span>
                    <textarea
                      v-model="statusNotes"
                      rows="1"
                      placeholder="Add context for this step"
                    ></textarea>
                  </label>
                </div>

                <div class="status-form__actions">
                  <button
                    class="primary-button status-form__save"
                    type="button"
                    :disabled="!selectedStatus || isSavingStatus"
                    @click="handleUpdateStatus"
                  >
                    <font-awesome-icon icon="check" />
                    {{ isSavingStatus ? 'Saving...' : 'Save update' }}
                  </button>
                </div>
              </div>
            </template>
          </ApplicationJourney>

          <div class="job-workspace">
            <section v-if="normalizedSkills.length" class="skills-lane">
              <div class="lane-heading skills-lane__heading">
                <div>
                  <h2>Skills</h2>
                  <p v-if="normalizedSkills.length">
                    {{ matchedSkillCount }} of {{ normalizedSkills.length }} matched
                  </p>
                </div>
              </div>
              <ul class="skill-list">
                <li
                  v-for="skill in normalizedSkills"
                  :key="skill.skill_name"
                  :class="{ 'skill-item--matched': skill.user_has }"
                >
                  <font-awesome-icon :icon="skill.user_has ? 'check' : 'circle'" />
                  <span>{{ skill.skill_name }}</span>
                  <small v-if="skill.years_required">{{ skill.years_required }}y</small>
                  <small v-if="skill.is_required" class="skill-required">Required</small>
                </li>
              </ul>
            </section>

            <main class="job-description-panel">
              <div class="section-heading job-description-heading">
                <h2>Job description</h2>
                <p
                  class="description-skill-helper"
                  :class="{ 'description-skill-helper--error': skillSelectionFeedbackType === 'error' }"
                  role="status"
                >
                  <font-awesome-icon
                    :icon="skillSelectionFeedbackType === 'error' ? 'times' : skillSelectionFeedback ? 'check' : 'plus'"
                  />
                  {{ skillSelectionFeedback || 'Select a skill in the job description to add it to your skills.' }}
                </p>
                <a
                  v-if="job?.job_url"
                  :href="job.job_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-link"
                >
                  View original
                  <font-awesome-icon icon="external-link" />
                </a>
              </div>

              <div
                ref="descriptionViewportRef"
                class="job-description-viewport"
                :class="{ 'job-description-viewport--expanded': isDescriptionExpanded }"
              >
                <div
                  ref="descriptionContentRef"
                  v-html="sanitizedDescription"
                  class="job-description"
                  tabindex="-1"
                  @mouseup="handleDescriptionSelection"
                ></div>
              </div>
              <button
                v-if="isDescriptionOverflowing || isDescriptionExpanded"
                type="button"
                class="show-description-button"
                :aria-expanded="isDescriptionExpanded"
                @click="toggleDescription"
              >
                {{ isDescriptionExpanded ? 'Show less' : 'Show more' }}
                <font-awesome-icon
                  icon="chevron-down"
                  :class="{ 'show-description-button__icon--open': isDescriptionExpanded }"
                />
              </button>
            </main>

            <Transition name="accordion">
              <section v-if="isTrackedJob && isNotesOpen" id="job-notes-panel" class="notes-lane">
                <div class="lane-heading">
                  <div>
                    <h2>Notes</h2>
                    <p v-if="!isEditingGeneralNotes" class="notes-preview">{{ generalNotes || 'No notes yet.' }}</p>
                    <p v-else>Private application notes</p>
                  </div>
                  <button class="secondary-button secondary-button--small" type="button" @click="toggleGeneralNotesEdit">
                    {{ isEditingGeneralNotes ? 'Save' : 'Edit' }}
                  </button>
                </div>
                <textarea
                  v-if="isEditingGeneralNotes"
                  v-model="generalNotes"
                  rows="5"
                  placeholder="Add notes about this application"
                ></textarea>
              </section>
            </Transition>
          </div>

          <footer class="job-footer">
            <div class="job-footer__actions">
              <button
                v-if="isTrackedJob"
                class="footer-link"
                type="button"
                :aria-expanded="isNotesOpen"
                aria-controls="job-notes-panel"
                @click="isNotesOpen = !isNotesOpen"
              >
                Notes
                <font-awesome-icon icon="chevron-down" :class="{ 'footer-link__icon--open': isNotesOpen }" />
              </button>
              <button class="secondary-button" type="button" @click="closeModal">Close</button>
              <a
                v-if="canApply"
                :href="job.job_url"
                target="_blank"
                rel="noopener noreferrer"
                class="primary-button"
              >
                Apply
                <font-awesome-icon icon="arrow-right" />
              </a>
            </div>
          </footer>
        </article>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { ref, computed, nextTick, watch, onMounted, onUnmounted, type PropType } from 'vue';
import DOMPurify from 'dompurify';
import api from '../services/api';
import ApplicationJourney from './ApplicationJourney.vue';
import type { ProcessStep } from '../types';
import {
  CLOSED_APPLICATION_STATUSES,
  getApplicationStageIndex,
} from '../utils/applicationStages';
import { toLocalDateInputValue } from '../utils/date';

interface LocalProcessStep extends ProcessStep {
  process_time?: string | null;
  notes?: string | null;
}

interface NormalizedSkill {
  skill_name: string;
  years_required: number | null;
  user_has: boolean;
  is_required: boolean;
}

interface JobFact {
  key: string;
  icon: string;
  value: string;
}

interface SelectionAction {
  top: number;
  left: number;
}

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
];

export default {
  name: 'JobDetailModal',
  components: { ApplicationJourney },
  props: {
    job: { type: Object, required: true },
    isOpen: { type: Boolean, default: false },
    statuses: { type: Array as PropType<string[]>, default: () => [] },
    userSkills: { type: Array, default: () => [] },
    jobSearchId: { type: Number, default: null },
  },
  emits: ['close', 'status-updated', 'skill-added'],
  setup(
    props: { job: any; isOpen: boolean; statuses: string[]; userSkills: any[]; jobSearchId: number | null },
    { emit }: { emit: (event: string, ...args: unknown[]) => void }
  ) {
    const statusDate = ref<string>(toLocalDateInputValue());
    const selectedTimeSlot = ref<string>('');
    const customTime = ref<string>('');
    const selectedStatus = ref<string>('');
    const statusNotes = ref<string>('');
    const isSavingStatus = ref<boolean>(false);
    const generalNotes = ref<string>('');
    const isEditingGeneralNotes = ref<boolean>(false);
    const isNotesOpen = ref<boolean>(false);
    const localProcessSteps = ref<LocalProcessStep[]>([]);
    const isApplicationPanelOpen = ref<boolean>(false);
    const isDescriptionExpanded = ref<boolean>(false);
    const isDescriptionOverflowing = ref<boolean>(false);
    const descriptionContentRef = ref<HTMLElement | null>(null);
    const descriptionViewportRef = ref<HTMLElement | null>(null);
    const jobModalRef = ref<HTMLElement | null>(null);
    const mainCloseButtonRef = ref<HTMLButtonElement | null>(null);
    const skillDialogRef = ref<HTMLElement | null>(null);
    const skillYearsInputRef = ref<HTMLInputElement | null>(null);
    const selectionAction = ref<SelectionAction | null>(null);
    const selectedSkillText = ref<string>('');
    const selectedSkillYears = ref<string>('');
    const isAddingSelectedSkill = ref<boolean>(false);
    const isSkillExperienceDialogOpen = ref<boolean>(false);
    const skillExperienceError = ref<string>('');
    const skillSelectionFeedback = ref<string>('');
    const skillSelectionFeedbackType = ref<'success' | 'error'>('success');
    const locallyAddedSkills = ref<Map<string, string>>(new Map());

    let descriptionResizeObserver: ResizeObserver | null = null;
    let previousMainFocus: HTMLElement | null = null;
    let previousSkillFocus: HTMLElement | null = null;
    let previousBodyOverflow = '';

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const trapFocus = (event: KeyboardEvent, container: HTMLElement | null): void => {
      if (event.key !== 'Tab' || !container) return;
      const focusableElements = Array.from(
        container.querySelectorAll<HTMLElement>(focusableSelector)
      ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

      if (!focusableElements.length) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleJobModalKeydown = (event: KeyboardEvent): void => {
      if (isSkillExperienceDialogOpen.value) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }
      trapFocus(event, jobModalRef.value);
    };

    const handleSkillDialogKeydown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSkillExperienceDialog();
        return;
      }
      trapFocus(event, skillDialogRef.value);
    };

    const resetStatusForm = (): void => {
      statusDate.value = toLocalDateInputValue();
      selectedTimeSlot.value = '';
      customTime.value = '';
      selectedStatus.value = '';
      statusNotes.value = '';
    };

    const updateDescriptionOverflow = (): void => {
      if (isDescriptionExpanded.value) return;
      const contentHeight = descriptionContentRef.value?.scrollHeight || 0;
      const viewportHeight = descriptionViewportRef.value?.clientHeight || 250;
      isDescriptionOverflowing.value = contentHeight > viewportHeight + 1;
    };

    const measureDescriptionOverflow = async (): Promise<void> => {
      await nextTick();
      updateDescriptionOverflow();
      descriptionResizeObserver?.disconnect();
      if (descriptionViewportRef.value) descriptionResizeObserver?.observe(descriptionViewportRef.value);
    };

    watch(
      () => props.job,
      (newJob) => {
        resetStatusForm();
        generalNotes.value = newJob?.notes || '';
        isEditingGeneralNotes.value = false;
        isNotesOpen.value = false;
        isApplicationPanelOpen.value = false;
        const effectiveStatus = newJob?.process_steps?.[0]?.process_type
          || newJob?.latest_status
          || newJob?.latest_process_type
          || '';
        isDescriptionExpanded.value = ['Found', 'Matched'].includes(effectiveStatus);
        selectionAction.value = null;
        selectedSkillText.value = '';
        selectedSkillYears.value = '';
        isSkillExperienceDialogOpen.value = false;
        skillExperienceError.value = '';
        skillSelectionFeedback.value = '';
        locallyAddedSkills.value = new Map();
        localProcessSteps.value = newJob?.process_steps
          ? newJob.process_steps.map((step: ProcessStep) => ({
            ...step,
            process_time: step.process_time || null,
            notes: step.notes || null,
          }))
          : [];
        measureDescriptionOverflow();
      },
      { immediate: true }
    );

    watch(
      () => props.isOpen,
      (isOpen) => {
        if (isOpen) {
          previousMainFocus = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
          previousBodyOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          measureDescriptionOverflow();
          nextTick(() => mainCloseButtonRef.value?.focus());
        } else {
          selectionAction.value = null;
          descriptionResizeObserver?.disconnect();
          document.body.style.overflow = previousBodyOverflow;
          nextTick(() => previousMainFocus?.focus());
        }
      },
      { immediate: true }
    );

    watch(isSkillExperienceDialogOpen, (isOpen) => {
      if (isOpen) {
        previousSkillFocus = document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
        nextTick(() => skillYearsInputRef.value?.focus());
      } else {
        nextTick(() => {
          if (previousSkillFocus?.isConnected) previousSkillFocus.focus();
          else descriptionContentRef.value?.focus();
        });
      }
    });

    const sanitizedDescription = computed((): string => {
      if (!props.job?.description) return '<p>No description available.</p>';
      let description: string = props.job.description;
      if (description.includes('&lt;') || description.includes('&amp;')) {
        const textArea = document.createElement('textarea');
        textArea.innerHTML = description;
        description = textArea.value;
      }
      return DOMPurify.sanitize(description);
    });

    const normalizedSkills = computed<NormalizedSkill[]>(() => {
      const job = props.job;
      if (!job) return [];
      const persistedSkills = Array.isArray(job.skills) ? job.skills : [];
      const skills: NormalizedSkill[] = persistedSkills.map((skill: any) => ({
        skill_name: skill.skill_name,
        years_required: skill.years_required ?? null,
        user_has: skill.user_has_skill
          || locallyAddedSkills.value.has(skill.skill_name.toLowerCase()),
        is_required: skill.is_required ?? false,
      }));

      locallyAddedSkills.value.forEach((skillName, normalizedName) => {
        const existingSkill = skills.find((skill) => skill.skill_name.toLowerCase() === normalizedName);
        if (existingSkill) {
          existingSkill.user_has = true;
        } else {
          skills.push({
            skill_name: skillName,
            years_required: null,
            user_has: true,
            is_required: false,
          });
        }
      });

      return skills.sort((a, b) => {
        const requiredDifference = Number(b.is_required) - Number(a.is_required);
        if (requiredDifference) return requiredDifference;
        const matchDifference = Number(b.user_has) - Number(a.user_has);
        if (matchDifference) return matchDifference;
        return a.skill_name.localeCompare(b.skill_name);
      });
    });

    const matchedSkillCount = computed<number>(() => normalizedSkills.value.filter((skill) => skill.user_has).length);
    const matchPercentage = computed<number | null>(() => {
      if (!normalizedSkills.value.length) return null;
      return Math.round((matchedSkillCount.value / normalizedSkills.value.length) * 100);
    });

    const currentStatus = computed<string>(() => (
      localProcessSteps.value[0]?.process_type
      || props.job?.latest_status
      || props.job?.latest_process_type
      || ''
    ));

    const isTrackedJob = computed<boolean>(() => Boolean(
      props.job?.application_date
      || props.job?.latest_status
      || props.job?.latest_process_type
      || localProcessSteps.value.length
    ));

    const applicationStageIndex = computed<number>(() => (
      getApplicationStageIndex(currentStatus.value, localProcessSteps.value)
    ));
    const isClosedApplication = computed<boolean>(() => (
      CLOSED_APPLICATION_STATUSES.includes(currentStatus.value) || currentStatus.value === 'Accepted'
    ));
    const canAddStep = computed<boolean>(() => (
      isTrackedJob.value && !isClosedApplication.value && props.statuses.length > 0
    ));
    const canApply = computed<boolean>(() => Boolean(
      props.job?.job_url
      && applicationStageIndex.value === 0
      && !isClosedApplication.value
    ));

    const companyInitials = computed<string>(() => {
      const words = String(props.job?.company || '').trim().split(/\s+/).filter(Boolean);
      return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase() || 'J';
    });

    const workMetadata = computed<{ jobType: string; arrangement: string }>(() => {
      const workTypeLabels: Record<string, string> = {
        '0': 'On-site',
        '1': 'Remote',
        '2': 'Hybrid',
      };
      const rawWorkType = props.job?.work_type;
      const rawLabel = rawWorkType === null || rawWorkType === undefined
        ? ''
        : workTypeLabels[String(rawWorkType)] || String(rawWorkType).trim();

      const jobTypePatterns = [
        { pattern: /\bfull[ -]?time\b/i, label: 'Full-time' },
        { pattern: /\bpart[ -]?time\b/i, label: 'Part-time' },
        { pattern: /\bcontract(?:or)?\b/i, label: 'Contract' },
        { pattern: /\btemporary\b/i, label: 'Temporary' },
        { pattern: /\bintern(?:ship)?\b/i, label: 'Internship' },
        { pattern: /\bfreelance\b/i, label: 'Freelance' },
      ];
      const arrangementPatterns = [
        { pattern: /\bremote\b/i, label: 'Remote' },
        { pattern: /\bhybrid\b/i, label: 'Hybrid' },
        { pattern: /\bon[ -]?site\b/i, label: 'On-site' },
      ];

      const detectedJobType = jobTypePatterns.find(({ pattern }) => pattern.test(rawLabel));
      const detectedArrangement = arrangementPatterns.find(({ pattern }) => pattern.test(rawLabel));
      const jobType = props.job?.is_contract ? 'Contract' : detectedJobType?.label || '';
      const arrangement = detectedArrangement?.label || (!detectedJobType ? rawLabel : '');

      return { jobType, arrangement };
    });

    const jobFacts = computed<JobFact[]>(() => {
      const facts: Array<JobFact | null> = [
        props.job?.location
          ? { key: 'location', icon: 'location-dot', value: String(props.job.location) }
          : null,
        workMetadata.value.jobType
          ? { key: 'job-type', icon: 'file-contract', value: workMetadata.value.jobType }
          : null,
        workMetadata.value.arrangement
          ? { key: 'arrangement', icon: 'house-laptop', value: workMetadata.value.arrangement }
          : null,
        props.job?.salary_range || props.job?.salary
          ? { key: 'salary', icon: 'money-bill-wave', value: String(props.job.salary_range || props.job.salary) }
          : null,
        props.job?.overall_experience_required != null
          ? {
            key: 'experience',
            icon: 'briefcase',
            value: `${props.job.overall_experience_required}+ years experience`,
          }
          : null,
      ];

      return facts.filter((fact): fact is JobFact => fact !== null);
    });

    const formatDate = (dateString: string): string => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const differenceInDays = Math.ceil(Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      if (differenceInDays === 1) return '1 day ago';
      if (differenceInDays < 7) return `${differenceInDays} days ago`;
      if (differenceInDays < 30) return `${Math.floor(differenceInDays / 7)} weeks ago`;
      return date.toLocaleDateString();
    };

    const formatTimeSlot = (time: string): string => {
      const [hourString, minute] = time.split(':');
      const hour = Number(hourString);
      const period = hour >= 12 ? 'PM' : 'AM';
      return `${hour % 12 || 12}:${minute} ${period}`;
    };

    const timeSlots = computed(() => TIME_SLOTS.map((value) => ({ value, label: formatTimeSlot(value) })));
    const isCustomTime = computed(() => selectedTimeSlot.value === 'custom');
    const selectedStatusTime = computed(() => (isCustomTime.value ? customTime.value : selectedTimeSlot.value));

    const useTimeSlots = (): void => {
      selectedTimeSlot.value = '';
      customTime.value = '';
    };

    const handleUpdateStatus = async (): Promise<void> => {
      if (!selectedStatus.value || isSavingStatus.value) return;
      isSavingStatus.value = true;
      try {
        const response = await api.updateJobStatus(
          props.job.id,
          selectedStatus.value,
          statusDate.value,
          selectedStatusTime.value,
          statusNotes.value
        );
        localProcessSteps.value.unshift(response.data);
        emit('status-updated', props.job.id, selectedStatus.value, statusDate.value);
        isDescriptionExpanded.value = ['Found', 'Matched'].includes(selectedStatus.value);
        if (!isDescriptionExpanded.value) measureDescriptionOverflow();
        resetStatusForm();
        isApplicationPanelOpen.value = false;
      } finally {
        isSavingStatus.value = false;
      }
    };

    const toggleGeneralNotesEdit = async (): Promise<void> => {
      if (isEditingGeneralNotes.value && props.job?.id) {
        await api.updateJob(props.job.id, { notes: generalNotes.value });
        emit('status-updated', props.job.id);
      }
      isEditingGeneralNotes.value = !isEditingGeneralNotes.value;
    };

    const toggleDescription = (): void => {
      isDescriptionExpanded.value = !isDescriptionExpanded.value;
      if (!isDescriptionExpanded.value) measureDescriptionOverflow();
    };

    const handleDescriptionSelection = (): void => {
      const selection = window.getSelection();
      const descriptionElement = descriptionContentRef.value;
      if (
        !selection
        || selection.isCollapsed
        || !selection.anchorNode
        || !selection.focusNode
        || !descriptionElement?.contains(selection.anchorNode)
        || !descriptionElement.contains(selection.focusNode)
      ) {
        selectionAction.value = null;
        return;
      }

      const text = selection.toString().replace(/\s+/g, ' ').trim();
      if (text.length < 2 || text.length > 80) {
        selectionAction.value = null;
        return;
      }

      const rect = selection.getRangeAt(0).getBoundingClientRect();
      selectedSkillText.value = text;
      skillSelectionFeedback.value = '';
      selectionAction.value = {
        top: Math.min(rect.bottom + 8, window.innerHeight - 44),
        left: Math.min(Math.max(rect.left, 12), window.innerWidth - 122),
      };
    };

    const handleSelectionChange = (): void => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) selectionAction.value = null;
    };

    const openSkillExperienceDialog = (): void => {
      if (!selectedSkillText.value.trim()) return;
      selectionAction.value = null;
      selectedSkillYears.value = '';
      skillExperienceError.value = '';
      isSkillExperienceDialogOpen.value = true;
      window.getSelection()?.removeAllRanges();
    };

    const closeSkillExperienceDialog = (): void => {
      if (isAddingSelectedSkill.value) return;
      isSkillExperienceDialogOpen.value = false;
      selectedSkillText.value = '';
      selectedSkillYears.value = '';
      skillExperienceError.value = '';
    };

    const handleAddSelectedSkill = async (): Promise<void> => {
      const skillName = selectedSkillText.value.trim();
      if (!skillName || isAddingSelectedSkill.value) return;

      const parsedYears = Number(selectedSkillYears.value);
      if (selectedSkillYears.value === '' || !Number.isInteger(parsedYears) || parsedYears < 0 || parsedYears > 50) {
        skillExperienceError.value = 'Enter a whole number from 0 to 50.';
        return;
      }

      selectionAction.value = null;
      skillExperienceError.value = '';
      isAddingSelectedSkill.value = true;
      try {
        const skillResponse = await api.addSkill(skillName, true);
        const profileResponse = await api.addMySkill(skillResponse.data.skill_id, parsedYears, props.job.id);
        const alreadyAdded = (profileResponse.data as any)?.message === 'Skill already in your profile';
        locallyAddedSkills.value = new Map(locallyAddedSkills.value).set(
          profileResponse.data.job_skill?.skill_name.toLowerCase() || skillResponse.data.skill_name.toLowerCase(),
          profileResponse.data.job_skill?.skill_name || skillResponse.data.skill_name
        );
        skillSelectionFeedback.value = alreadyAdded
          ? `${skillResponse.data.skill_name} updated to ${parsedYears} years of experience.`
          : `${skillResponse.data.skill_name} added with ${parsedYears} years of experience.`;
        skillSelectionFeedbackType.value = 'success';
        emit('skill-added', {
          skill_id: skillResponse.data.skill_id,
          skill_name: skillResponse.data.skill_name,
          years_experience: parsedYears,
        });
        isSkillExperienceDialogOpen.value = false;
      } catch (error: any) {
        skillExperienceError.value = error.response?.data?.error || 'Could not add that skill.';
      } finally {
        isAddingSelectedSkill.value = false;
        if (!isSkillExperienceDialogOpen.value) {
          selectedSkillText.value = '';
          selectedSkillYears.value = '';
        }
      }
    };

    onMounted(() => {
      document.addEventListener('selectionchange', handleSelectionChange);
      if (typeof ResizeObserver !== 'undefined') {
        descriptionResizeObserver = new ResizeObserver(updateDescriptionOverflow);
        measureDescriptionOverflow();
      }
    });
    onUnmounted(() => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      descriptionResizeObserver?.disconnect();
      document.body.style.overflow = previousBodyOverflow;
    });

    const closeModal = (): void => {
      resetStatusForm();
      isApplicationPanelOpen.value = false;
      isNotesOpen.value = false;
      isDescriptionExpanded.value = false;
      selectionAction.value = null;
      isSkillExperienceDialogOpen.value = false;
      emit('close');
    };

    return {
      statusDate,
      selectedTimeSlot,
      customTime,
      selectedStatus,
      statusNotes,
      isSavingStatus,
      generalNotes,
      isEditingGeneralNotes,
      isNotesOpen,
      localProcessSteps,
      isApplicationPanelOpen,
      isDescriptionExpanded,
      isDescriptionOverflowing,
      descriptionContentRef,
      descriptionViewportRef,
      jobModalRef,
      mainCloseButtonRef,
      skillDialogRef,
      skillYearsInputRef,
      selectionAction,
      selectedSkillText,
      selectedSkillYears,
      isAddingSelectedSkill,
      isSkillExperienceDialogOpen,
      skillExperienceError,
      skillSelectionFeedback,
      skillSelectionFeedbackType,
      sanitizedDescription,
      normalizedSkills,
      matchedSkillCount,
      matchPercentage,
      currentStatus,
      isTrackedJob,
      isClosedApplication,
      canAddStep,
      canApply,
      companyInitials,
      jobFacts,
      formatDate,
      timeSlots,
      isCustomTime,
      useTimeSlots,
      handleUpdateStatus,
      toggleGeneralNotesEdit,
      toggleDescription,
      handleDescriptionSelection,
      openSkillExperienceDialog,
      closeSkillExperienceDialog,
      handleAddSelectedSkill,
      handleJobModalKeydown,
      handleSkillDialogKeydown,
      closeModal,
    };
  },
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active { transition: opacity 180ms ease; }
.modal-enter-active .job-modal,
.modal-leave-active .job-modal { transition: transform 180ms ease; }
.modal-enter-from,
.modal-leave-to { opacity: 0; }
.modal-enter-from .job-modal,
.modal-leave-to .job-modal { transform: translateY(8px); }

.job-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  background: rgba(17, 24, 39, 0.44);
  backdrop-filter: blur(4px);
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

.job-modal {
  --optyfind-brand: #0a8bc2;
  --optyfind-brand-action: #08709e;
  --optyfind-brand-hover: #075a7e;
  display: flex;
  width: min(1040px, calc(100vw - 40px));
  height: min(760px, calc(100vh - 40px));
  max-height: calc(100vh - 40px);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #d1d5db;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 70px rgba(17, 24, 39, 0.18);
  transition: height 220ms ease;
}

.job-modal--expanded {
  height: min(1140px, calc(100vh - 40px));
}

.job-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 26px 28px 22px;
}

.job-header__main,
.job-header__actions,
.job-facts,
.job-footer,
.job-footer__actions,
.section-heading { display: flex; align-items: center; }

.job-header__main { min-width: 0; align-items: flex-start; gap: 16px; }
.company-mark {
  display: flex;
  width: 48px;
  height: 48px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  background: var(--optyfind-brand);
  color: #fff;
  font-size: 14px;
  font-weight: 750;
  letter-spacing: 0.08em;
}

.job-header__copy { min-width: 0; }
.job-company { color: var(--optyfind-brand-hover); font-size: 16px; font-weight: 700; line-height: 1.15; }
.job-header h1 {
  margin-top: 5px;
  color: #111827;
  font-size: clamp(20px, 2vw, 24px);
  font-weight: 700;
  letter-spacing: -0.035em;
  line-height: 1.08;
}
.job-posted { margin-top: 7px; color: #6b7280; font-size: 12px; }
.job-header__actions { align-items: flex-start; gap: 18px; }
.match-score { min-width: 78px; border-left: 2px solid var(--optyfind-brand); padding-left: 12px; }
.match-score span,
.match-score small { display: block; }
.match-score span {
  color: #111827;
  font-size: 23px;
  font-weight: 750;
  letter-spacing: -0.04em;
  line-height: 1;
}
.match-score small {
  margin-top: 4px;
  color: #6b7280;
  font-size: 10px;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.icon-button {
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
}
.icon-button:hover { border-color: #111827; color: #111827; }

.job-facts {
  min-height: 48px;
  flex-wrap: wrap;
  border-top: 1px solid #e5e7eb;
  padding: 10px 28px;
  color: #4b5563;
  font-size: 12px;
}
.job-fact { display: inline-flex; align-items: center; }
.job-fact + .job-fact::before {
  margin: 0 16px;
  color: #d1d5db;
  content: '|';
}
.job-fact__icon {
  width: 13px;
  margin-right: 7px;
  color: var(--optyfind-brand);
}

.job-workspace {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
  overflow: hidden;
  padding: 14px 28px 26px;
}
.job-modal > :deep(.journey) {
  flex: 0 0 auto;
  margin: 14px 28px 0;
}

.skills-lane,
.notes-lane,
.job-description-panel {
  flex: 0 0 auto;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}

.skills-lane,
.notes-lane {
  margin-bottom: 10px;
  padding: 16px 18px;
}

.skills-lane {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
}

.notes-lane textarea {
  margin-top: 14px;
}

.notes-lane {
  margin-top: 10px;
  margin-bottom: 0;
}

.notes-preview {
  max-width: 680px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-description-panel {
  display: flex;
  min-height: 120px;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 20px 22px 16px;
}

.section-heading,
.lane-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; }
.section-heading { border-bottom: 1px solid #e5e7eb; padding-bottom: 14px; }
.section-heading h2,
.lane-heading h2 { color: #111827; font-size: 13px; font-weight: 750; }
.section-heading p,
.lane-heading p { margin-top: 3px; color: #6b7280; font-size: 11px; }

.job-description-heading {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(300px, 2fr) minmax(120px, 1fr);
  align-items: center;
}
.job-description-heading .text-link { justify-self: end; }
.description-skill-helper {
  display: inline-flex;
  align-items: center;
  justify-self: center;
  gap: 7px;
  margin: 0;
  color: #64748b;
  font-size: 10px;
  line-height: 1.4;
  text-align: center;
}
.description-skill-helper svg { width: 9px; color: var(--optyfind-brand); }
.description-skill-helper--error { color: #b91c1c; }
.description-skill-helper--error svg { color: #b91c1c; }

.text-link {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 7px;
  color: var(--optyfind-brand-action);
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.text-link:hover { text-decoration: underline; }

.job-description {
  max-width: 76ch;
  padding-top: 8px;
  color: #374151;
  font-size: 14px;
  line-height: 1.72;
}
.job-description :deep(h1),
.job-description :deep(h2),
.job-description :deep(h3) {
  margin: 1.8em 0 0.55em;
  color: #111827;
  font-weight: 700;
  letter-spacing: -0.015em;
}
.job-description :deep(h1) { font-size: 22px; }
.job-description :deep(h2) { font-size: 18px; }
.job-description :deep(h3) { font-size: 15px; }
.job-description :deep(p),
.job-description :deep(ul),
.job-description :deep(ol) { margin: 0.85em 0; }
.job-description :deep(ul),
.job-description :deep(ol) { padding-left: 1.35em; }
.job-description :deep(li) { margin: 0.4em 0; }
.job-description :deep(a) { color: var(--optyfind-brand-action); text-decoration: underline; }
.job-description :deep(strong),
.job-description :deep(b) { color: #111827; font-weight: 700; }

.job-description-viewport {
  position: relative;
  min-height: 72px;
  flex: 1 1 250px;
  max-height: 250px;
  overflow: hidden;
}

.job-description-viewport::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 76px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff 88%);
  pointer-events: none;
  content: '';
}

.job-description-viewport--expanded {
  max-height: none;
  overflow-y: auto;
}

.job-description-viewport--expanded::after {
  display: none;
}

.show-description-button {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  color: var(--optyfind-brand-action);
  font-size: 12px;
  font-weight: 700;
}

.show-description-button svg {
  transition: transform 200ms ease;
}

.show-description-button__icon--open {
  transform: rotate(180deg);
}

.skill-list {
  display: flex;
  min-width: 0;
  flex-wrap: nowrap;
  gap: 7px;
  overflow-x: auto;
  padding-bottom: 2px;
}
.skill-list li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d9dee5;
  border-left: 3px solid #cbd5e1;
  border-radius: 5px;
  background: #f7f7f8;
  padding: 7px 10px 7px 9px;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.skill-list svg { width: 8px; color: #9ca3af; }
.skill-list .skill-item--matched {
  border-color: #b8e3f4;
  border-left-color: var(--optyfind-brand-action);
  background: #eef9fd;
  color: #075a7e;
}
.skill-item--matched svg { color: var(--optyfind-brand-action); }
.skill-list small { color: #9ca3af; }
.skill-required { border-left: 1px solid #d1d5db; padding-left: 6px; font-weight: 700; }

.selection-add-button {
  position: fixed;
  z-index: 70;
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  gap: 7px;
  border: 1px solid var(--optyfind-brand-action, #08709e);
  border-radius: 7px;
  background: #08709e;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.2);
  padding: 7px 10px;
  color: #fff;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 11px;
  font-weight: 700;
}
.selection-add-button:hover { background: #075a7e; }
.selection-add-button:disabled { cursor: wait; opacity: 0.72; }

.skill-experience-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(17, 24, 39, 0.28);
  backdrop-filter: blur(2px);
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

.skill-experience-dialog {
  --optyfind-brand: #0a8bc2;
  --optyfind-brand-action: #08709e;
  --optyfind-brand-hover: #075a7e;
  display: grid;
  width: min(380px, calc(100vw - 40px));
  gap: 18px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 20px 48px rgba(17, 24, 39, 0.22);
  padding: 22px;
}
.skill-experience-dialog__eyebrow {
  margin-bottom: 4px;
  color: #08709e;
  font-size: 10px;
  font-weight: 700;
}
.skill-experience-dialog h2 {
  overflow-wrap: anywhere;
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
}
.skill-experience-dialog label > span {
  display: block;
  margin-bottom: 6px;
  color: #374151;
  font-size: 11px;
  font-weight: 700;
}
.skill-experience-dialog__error {
  margin-top: -8px;
  color: #b91c1c;
  font-size: 11px;
}
.skill-experience-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
}
.skill-experience-dialog__actions .primary-button,
.skill-experience-dialog__actions .secondary-button {
  min-height: 42px;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease, opacity 160ms ease;
}
.skill-experience-dialog__actions .primary-button {
  border-color: #08709e;
  background: #08709e;
  color: #fff;
}
.skill-experience-dialog__actions .primary-button:hover:not(:disabled) {
  border-color: #075a7e;
  background: #075a7e;
}
.skill-experience-dialog__actions .primary-button:focus-visible,
.skill-experience-dialog__actions .secondary-button:focus-visible {
  outline: 2px solid rgba(10, 139, 194, 0.34);
  outline-offset: 2px;
}
.skill-experience-dialog__actions .secondary-button {
  border-color: #d1d5db;
  background: #fff;
  color: #374151;
}
.skill-experience-dialog__actions .secondary-button:hover:not(:disabled) {
  border-color: #9ca3af;
  background: #f7f7f8;
}

.empty-copy,
.closed-copy { color: #6b7280; font-size: 12px; line-height: 1.55; }
.status-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  gap: 16px;
}
.status-form__fields {
  display: grid;
  grid-template-columns: minmax(220px, 2fr) minmax(140px, 1fr) minmax(140px, 1fr);
  align-items: end;
  gap: 10px 12px;
}
.status-form label,
.status-form label > span:first-child { display: block; }
.status-form label > span:first-child {
  margin-bottom: 5px;
  color: #4b5563;
  font-size: 10px;
  font-weight: 700;
}
.status-form label small { color: #9ca3af; font-weight: 500; }
.status-form__note { grid-column: 1 / -1; }
.status-form__note textarea {
  min-height: 38px;
  resize: none;
}
.status-form__actions {
  display: flex;
  min-width: 132px;
  align-items: center;
  justify-content: center;
  border-left: 1px solid #d1d5db;
  padding-left: 16px;
}
.status-form__save {
  width: 100%;
  min-width: 116px;
}

input,
select,
textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  background: #fff;
  color: #111827;
  font-family: inherit;
  font-size: 12px;
}
input,
select { min-height: 38px; padding: 8px 9px; }
textarea { resize: vertical; padding: 9px; line-height: 1.5; }
input:focus,
select:focus,
textarea:focus {
  outline: 2px solid rgba(10, 139, 194, 0.18);
  border-color: var(--optyfind-brand);
  box-shadow: none;
}
.custom-time { display: flex; }
.custom-time button {
  border: 1px solid #d1d5db;
  border-left: 0;
  background: #fff;
  padding: 0 7px;
  color: var(--optyfind-brand-action);
  font-size: 10px;
  font-weight: 700;
}

.primary-button,
.secondary-button {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px solid var(--optyfind-brand-action);
  border-radius: 7px;
  padding: 9px 15px;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.primary-button { background: var(--optyfind-brand-action); color: #fff; }
.primary-button:hover { background: var(--optyfind-brand-hover); }
.primary-button:disabled {
  cursor: not-allowed;
  border-color: #d1d5db;
  background: #d1d5db;
}
.secondary-button { border-color: #d1d5db; background: #fff; color: #374151; }
.secondary-button:hover { border-color: #111827; color: #111827; }
.secondary-button--small { min-height: 30px; padding: 5px 10px; font-size: 10px; }

.accordion-enter-active,
.accordion-leave-active {
  max-height: 340px;
  overflow: hidden;
  transition: max-height 220ms ease, opacity 180ms ease, transform 220ms ease, margin 220ms ease, padding 220ms ease;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
  transform: translateY(-6px);
}

.job-footer {
  min-height: 66px;
  flex: 0 0 auto;
  justify-content: flex-end;
  gap: 18px;
  border-top: 1px solid #d1d5db;
  background: #fff;
  padding: 12px 28px;
}
.job-footer__actions { gap: 10px; }
.footer-link {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  gap: 8px;
  padding: 8px 6px;
  color: var(--optyfind-brand-action);
  font-size: 12px;
  font-weight: 700;
}
.footer-link:hover { color: var(--optyfind-brand-hover); }
.footer-link svg { font-size: 10px; transition: transform 180ms ease; }
.footer-link__icon--open { transform: rotate(180deg); }

@media (max-width: 820px) {
  .status-form__fields {
    grid-template-columns: 1fr 1fr;
  }

  .status-form__status,
  .status-form__note {
    grid-column: 1 / -1;
  }
}

@media (max-width: 640px) {
  .job-modal-backdrop { align-items: stretch; padding: 0; }
  .job-modal,
  .job-modal--expanded {
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    border: 0;
    border-radius: 12px 12px 0 0;
  }
  .job-header { padding: 20px 18px 16px; }
  .company-mark { width: 42px; height: 42px; }
  .job-company { font-size: 14px; }
  .job-header h1 { font-size: 18px; }
  .match-score { display: none; }
  .job-facts { padding: 10px 18px; }
  .job-fact + .job-fact::before { margin: 0 10px; }
  .job-modal > :deep(.journey) { margin: 12px 18px 0; }
  .job-workspace { padding: 10px 18px 22px; }
  .skills-lane,
  .notes-lane { padding: 14px; }
  .skills-lane { display: block; }
  .skills-lane__heading { margin-bottom: 10px; }
  .status-form { gap: 12px; }
  .status-form__fields { grid-template-columns: 1fr; }
  .status-form__status,
  .status-form__note { grid-column: 1; }
  .status-form__actions {
    min-width: 104px;
    padding-left: 12px;
  }
  .status-form__save {
    min-width: 92px;
    padding-right: 10px;
    padding-left: 10px;
  }
  .job-description-panel { padding: 18px 16px 14px; }
  .job-workspace { overflow-y: auto; }
  .job-description-viewport--expanded { overflow: visible; }
  .section-heading { flex-direction: column; gap: 10px; }
  .job-description-heading {
    display: grid;
    grid-template-columns: 1fr auto;
  }
  .description-skill-helper {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-self: start;
    text-align: left;
  }
  .job-footer { padding: 10px 18px; }
  .job-footer__actions,
  .job-footer__actions > * { flex: 1 1 auto; }
}

@media (prefers-reduced-motion: reduce) {
  .job-modal,
  .accordion-enter-active,
  .accordion-leave-active,
  .show-description-button svg,
  .footer-link svg {
    transition: none;
  }
}
</style>
